import {
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Box,
} from "@mui/material";
import useStyles from "./styles";
import { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import {
  OpenInNewOutlined,
  RouterOutlined,
  StorageOutlined,
  TerminalOutlined,
} from "@mui/icons-material";
import HostsModal from "./modalListPreview";
import { useNavigate } from "react-router-dom";

const ENABLE_REMOTE_ACCESS = true;

export default function PreviewHostsConfig({
  manipulateCpes = [],
  manipulateOnus = [],
  manipulateLinks = [],
  manipulateSessions = [],
  availableHosts = {},
  selectedService = {},
  clienteInfo = {},
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();

  // Estados que armazenarão os dados já formatados para a tela
  const [cpesFormatted, setCpesFormatted] = useState([]);
  const [sessionFormatted, setSessionFormatted] = useState([]);
  const [linksFormatted, setLinksFormatted] = useState([]);

  // Estados do Modal
  const [openHostsModal, setOpenHostsModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedInterface, setSelectedInterface] = useState([]);

  // Paletas de Cores
  const colorsONU = [
    theme.palette.colors.blue.medium,
    theme.palette.colors.yellow.darker,
    theme.palette.colors.red.darker,
    theme.palette.colors.pink.light,
    theme.palette.colors.gray.dark,
    theme.palette.colors.green.light,
    theme.palette.colors.pink.medium,
    theme.palette.colors.orange.medium,
    theme.palette.colors.purple.medium,
  ];

  const GROUP_COLORS = {
    "SWITCHES HUAWEI": theme.palette.colors.pink.medium,
    "SWITCHES DATACOM": theme.palette.colors.green.light,
    "SWITCHES MIKROTIK": theme.palette.colors.purple.medium,
    DEFAULT: theme.palette.colors.gray.dark,
  };

  const getGroupColor = (groupName) => {
    return GROUP_COLORS[groupName] || GROUP_COLORS.DEFAULT;
  };

  // ==========================================================================
  // LÓGICA DE DADOS
  // ==========================================================================

  // Função de navegação para o terminal
  const handleRemoteAccessNavigation = (hostId) => {
    if (!ENABLE_REMOTE_ACCESS || !hostId) return;

    localStorage.setItem(
      "filterData",
      JSON.stringify({
        clienteId: clienteInfo?.id,
        serviceId: selectedService?.id,
        hostId: hostId,
      }),
    );
    navigate(`/interno/home/perfil/remoteAccess/${clienteInfo?.id}`);
  };

  /**
   * Função universal que transforma a lista crua da API em "Cards".
   * Cada item gera seu próprio card (sem mesclar grupos).
   */
  const formatDataToCards = (dataArray, hostType = "cpes") => {
    if (!dataArray || dataArray.length === 0 || !availableHosts?.[hostType])
      return [];

    // Mapeia item por item da array (cada host é tratado individualmente)
    return dataArray.map((item, index) => {
      const groupId = item.groupId;
      const groupInfo = availableHosts[hostType]
        ?.flatMap((h) => h?.groups || [])
        ?.find((g) => String(g?.groupid) === String(groupId));

      const groupName =
        groupInfo?.name ||
        item.modelo ||
        item.groups?.[0]?.name ||
        "Desconhecido";
      const color = getGroupColor(groupName);

      const hostsFormatted = [];
      const interfaces = [];

      // Cenário 1: "Sessões" (ainda usa a estrutura antiga agrupada em selectedHosts)
      if (item.selectedHosts && Array.isArray(item.selectedHosts)) {
        item.selectedHosts.forEach((sh) => {
          const hostId = sh.hostId || sh.hostid;
          hostsFormatted.push({
            id: sh.id || hostId || index,
            hostId: hostId,
            host: sh.host || "Host Desconhecido",
          });
          if (sh.hostsClienteInterfaces) {
            interfaces.push(...sh.hostsClienteInterfaces);
          }
        });
      }
      // Cenário 2: "CPEs" e "Links" (estrutura plana onde o próprio item é o host)
      else {
        const hostId = item.hostId || item.hostid;
        const hostNameInfo = availableHosts[hostType]?.find(
          (h) => String(h.hostid) === String(hostId),
        );

        hostsFormatted.push({
          id: item.id || hostId || index,
          hostId: hostId,
          host: item.host || hostNameInfo?.host || "Host Desconhecido",
        });
        if (item.hostsClienteInterfaces) {
          interfaces.push(...item.hostsClienteInterfaces);
        }
      }

      // Deduplica por nome de interface (string legada ou objeto { interface, tags })
      const ifaceKey = (i) => (typeof i === "string" ? i : i?.interface);
      const dedupedInterfaces = [
        ...new Map(interfaces.map((i) => [ifaceKey(i), i])).values(),
      ];

      return {
        id: item.id || `card-${index}`,
        groupName,
        color,
        hostsFormatted,
        interfaces: dedupedInterfaces,
      };
    });
  };

  useEffect(() => {
    setCpesFormatted(formatDataToCards(manipulateCpes, "cpes"));
    setSessionFormatted(formatDataToCards(manipulateSessions, "cpes"));
    setLinksFormatted(formatDataToCards(manipulateLinks, "cpes"));
  }, [manipulateCpes, manipulateSessions, manipulateLinks, availableHosts]);

  // ==========================================================================
  // FUNÇÕES DO MODAL
  // ==========================================================================
  const handleOpenHostsModal = (values, interfaces) => {
    setSelectedData(values);
    setSelectedInterface(interfaces);
    setOpenHostsModal(true);
  };

  const handleCloseHostsModal = () => {
    setOpenHostsModal(false);
    setSelectedData(null);
  };

  // ==========================================================================
  // CARD PADRÃO
  // CPEs, Sessões e Links têm a mesma estrutura
  // ==========================================================================
  const GenericConfigCard = ({ data, cardLabel, isCpeSection = false }) => {
    return (
      <Paper
        elevation={theme.palette.mode === "dark" ? 0 : 3}
        variant={theme.palette.mode === "light" ? "elevation" : "outlined"}
        sx={{
          ...styles.paperCard,
          minHeight: 213,
          borderTopColor: data.color,
        }}
      >
        {/* Cabeçalho do Card (Grupo e Label) */}
        <Box sx={{ ...styles.chipContained, backgroundColor: data.color }}>
          <Typography
            variant="h8"
            sx={{
              ...styles.nameChip,
              color: theme.palette.colors.white.lighter,
            }}
          >
            {data.groupName}
          </Typography>
        </Box>
        <Box sx={{ ...styles.chipOutlined, borderColor: data.color }}>
          <Typography variant="h8" sx={styles.nameChip}>
            {cardLabel}
          </Typography>
        </Box>

        {/* Corpo do Card: Hosts e Interfaces */}
        <Box sx={{ mb: 0.5 }}>
          <Box sx={styles.hostsSpacing}>
            {data.hostsFormatted.map((host, idx) => (
              <Box key={idx}>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                  Host:
                </Typography>
                <Box sx={styles.containerHost}>
                  <Box
                    sx={{
                      ...styles.modalStatusBall,
                      backgroundColor: `${data.color}70`,
                    }}
                  >
                    <Box
                      sx={{
                        ...styles.internalBall,
                        backgroundColor: data.color,
                      }}
                    />
                  </Box>
                  <Typography variant="body2">{host?.host}</Typography>
                  {ENABLE_REMOTE_ACCESS && host?.hostId && (
                    <Tooltip title="Acesso Remoto" placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoteAccessNavigation(host.hostId);
                        }}
                        sx={{ ml: "auto", p: 0.5 }}
                      >
                        <TerminalOutlined fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
              Interfaces:
            </Typography>

            {/* LÓGICA SOLICITADA: Se for host e não tiver interface, mostra a frase */}
            {data.interfaces.length === 0 && isCpeSection ? (
              <Typography
                variant="body2"
                sx={{ fontStyle: "italic", opacity: 0.7, ml: 0.5 }}
              >
                Todas serão utilizadas
              </Typography>
            ) : (
              // Caso tenha interfaces, desenha as 3 primeiras
              data.interfaces.slice(0, 3).map((item, idx) => {
                const ifaceName =
                  typeof item === "string" ? item : item?.interface;
                const ifaceTags =
                  typeof item === "string" ? [] : (item?.tags ?? []);
                return (
                  <Box key={idx} sx={styles.containerHost}>
                    <Box
                      sx={{
                        ...styles.modalStatusBall,
                        backgroundColor: `${data.color}70`,
                      }}
                    >
                      <Box
                        sx={{
                          ...styles.internalBall,
                          backgroundColor: data.color,
                        }}
                      />
                    </Box>
                    <Typography variant="body2">
                      {ifaceName?.trim()}
                    </Typography>
                    {ifaceTags.map((tag) => (
                      <Chip
                        key={tag?.id}
                        size="small"
                        label={tag?.tagName}
                        sx={{
                          height: 18,
                          ml: 0.5,
                          bgcolor: tag?.colorTag,
                          color: theme.palette.colors?.white?.lighter || "#fff",
                        }}
                      />
                    ))}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* Botão de Ver Todos (Apenas se houver mais de 3 interfaces) */}
        {data.interfaces.length > 3 && (
          <Button
            variant="text"
            onClick={() => handleOpenHostsModal(data, data.interfaces)}
            sx={styles.button}
            endIcon={<OpenInNewOutlined />}
          >
            Ver todas as Interfaces
          </Button>
        )}
      </Paper>
    );
  };

  // ==========================================================================
  // RENDERIZAÇÃO DA PÁGINA PRINCIPAL
  // ==========================================================================
  return (
    <Paper
      elevation={theme.palette.mode === "dark" ? 0 : 0}
      sx={styles.paperFluxo}
    >
      {/* SEÇÃO: CONFIGURAÇÕES DE HOSTS (CPEs) */}
      <Box sx={styles.paperSection}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StorageOutlined
            sx={{ mb: 0.2, fontSize: 20, color: theme.palette.primary.main }}
          />
          <Typography sx={styles.title}>Configurações de Hosts</Typography>
        </Box>

        {manipulateCpes?.length <= 0 && (
          <Box sx={styles.mensagemNodata}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Não há hosts cadastrados
            </Typography>
          </Box>
        )}

        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {cpesFormatted.map((cpe) => (
            // A largura aqui foi padronizada (md={4}) para ficarem lado a lado de forma elegante
            <Grid item xs={12} sm={6} md={4} key={cpe.id}>
              <GenericConfigCard
                data={cpe}
                cardLabel="Host"
                isCpeSection={true}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* SEÇÃO: CONFIGURAÇÕES DE ONUs  */}
      <Box sx={styles.paperSection}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <RouterOutlined
            sx={{ mb: 0.8, fontSize: 24, color: theme.palette.primary.main }}
          />
          <Typography sx={styles.title}>Configurações de ONUs</Typography>
        </Box>

        {manipulateOnus?.length <= 0 && (
          <Box sx={styles.mensagemNodata}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Não há ONUs cadastradas
            </Typography>
          </Box>
        )}

        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {manipulateOnus?.map((onu, index) => {
            const groupName =
              availableHosts?.onus
                ?.flatMap((h) => h?.groups || [])
                ?.find((g) => String(g?.groupid) === String(onu?.groupId))
                ?.name || "ONU Desconhecida";

            const resolvedHostId = onu?.hostid || onu?.hostId;
            const hostName =
              availableHosts?.onus?.find(
                (h) => String(h?.hostid) === String(resolvedHostId),
              )?.host ||
              onu?.host ||
              "-";

            const cardColor = colorsONU[index % colorsONU.length];

            return (
              <Grid item xs={12} sm={6} md={4} key={onu.id || index}>
                <Paper
                  elevation={theme.palette.mode == "dark" ? 0 : 3}
                  variant={
                    theme.palette.mode == "light" ? "elevation" : "outlined"
                  }
                  sx={{
                    ...styles.paperCard,
                    borderTopColor: cardColor,
                    minHeight: "100px",
                  }}
                >
                  <Box
                    sx={{ ...styles.chipContained, backgroundColor: cardColor }}
                  >
                    <Typography
                      variant="h8"
                      sx={{
                        ...styles.nameChip,
                        color: theme.palette.colors.white.lighter,
                      }}
                    >
                      {groupName}
                    </Typography>
                  </Box>
                  <Box sx={{ ...styles.chipOutlined, borderColor: cardColor }}>
                    <Typography variant="h8" sx={styles.nameChip}>
                      ONU
                    </Typography>
                  </Box>
                  <Box>
                    <Box>
                      <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Host:
                      </Typography>
                      <Box sx={styles.containerHost}>
                        {resolvedHostId && (
                          <Box
                            sx={{
                              ...styles.modalStatusBall,
                              backgroundColor: `${cardColor}70`,
                            }}
                          >
                            <Box
                              sx={{
                                ...styles.internalBall,
                                backgroundColor: cardColor,
                              }}
                            />
                          </Box>
                        )}
                        <Typography sx={{ fontSize: 14 }}>
                          {hostName}
                        </Typography>
                        {ENABLE_REMOTE_ACCESS && resolvedHostId && (
                          <Tooltip title="Acesso Remoto" placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoteAccessNavigation(resolvedHostId);
                              }}
                              sx={{ ml: "auto", p: 0.5 }}
                            >
                              <TerminalOutlined
                                fontSize="small"
                                color="primary"
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                        ONU:
                      </Typography>
                      <Box sx={styles.containerHost}>
                        <Box
                          sx={{
                            ...styles.modalStatusBall,
                            backgroundColor: `${cardColor}70`,
                          }}
                        >
                          <Box
                            sx={{
                              ...styles.internalBall,
                              backgroundColor: cardColor,
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 14 }}>
                          {onu?.onuItem}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* SEÇÃO: SESSÕES E LINKS */}
      <Grid container spacing={1}>
        {/* Lado Esquerdo: Sessões */}
        <Grid item xs={12} md={6}>
          <Box sx={styles.paperSection}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <StorageOutlined
                sx={{
                  mb: 0.2,
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography sx={styles.title}>
                Configurações de Sessões
              </Typography>
            </Box>
            {manipulateSessions?.length <= 0 && (
              <Box sx={styles.mensagemNodata}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Não há sessões cadastradas
                </Typography>
              </Box>
            )}
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              {sessionFormatted?.map((session) => (
                <Grid item xs={12} key={session?.id}>
                  <GenericConfigCard
                    data={session}
                    cardLabel="Sessões"
                    isCpeSection={false}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* Lado Direito: Links */}
        <Grid item xs={12} md={6}>
          <Box sx={styles.paperSection}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <StorageOutlined
                sx={{
                  mb: 0.2,
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography sx={styles.title}>Configurações de Link</Typography>
            </Box>
            {manipulateLinks?.length <= 0 && (
              <Box sx={styles.mensagemNodata}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Não há links cadastrados
                </Typography>
              </Box>
            )}
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              {linksFormatted?.map((links) => (
                <Grid item xs={12} key={links?.id}>
                  <GenericConfigCard
                    data={links}
                    cardLabel="Links"
                    isCpeSection={false}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* MODAL GLOBAL PARA LISTAS EXCEDENTES */}
      <HostsModal
        open={openHostsModal}
        onClose={handleCloseHostsModal}
        title={selectedData?.groupName}
        hosts={selectedData?.hostsFormatted || []}
        interfaces={selectedInterface}
        color={selectedData?.color}
        onNavigate={handleRemoteAccessNavigation}
        enableRemoteAccess={ENABLE_REMOTE_ACCESS}
      />
    </Paper>
  );
}
